import produce, { enableES5 } from 'immer';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { MutationStatus } from 'react-query';
import { candidateService } from '../../../api';
import { selectCandidateResitRegistration } from '../../../store/resitSlice';
import { useResitNavigation } from '../../../routes/ResitRoute';
import {
  CoordinatorResitAction,
  coordinatorResitReducer,
  INITIAL_COORDINATOR_RESIT,
} from './useCoordinatorResitData';
import { usePostStartProcessing } from '../../../api-refactored/group-resit-registration/usePostStartProcessing';
import {
  ProcessingFileStatus,
  useGetFeeProcessingStatus,
} from '../../../api-refactored/group-resit-registration/useGetFeeProcessingStatus';
import { usePostConfirmAndPay } from '../../../api-refactored/group-resit-registration/usePostConfirmAndPay';

enableES5();

export const useCoordinatorResitSteps = () => {
  const { t } = useTranslation();
  return [
    { label: t('resit-->CoordinatorResit.Select subjects') },
    { label: t('resit-->CoordinatorResit.Confirm Particulars') },
    { label: t('resit-->CoordinatorResit.Review (individual)') },
    { label: t('resit-->CoordinatorResit.Confirmation') },
  ];
};

export const useCoordinatorResit = (defaultAdding: boolean, resitUuid?: string) => {
  const history = useHistory();
  const { toCoordinatorResitRegister } = useResitNavigation();
  const [step, setStep] = useState(0);

  // getting data passing from csv upload
  const resitCandidateData = useSelector(selectCandidateResitRegistration);

  // local reducer
  const [state, dispatch] = useReducer(produce(coordinatorResitReducer), INITIAL_COORDINATOR_RESIT);

  // even it's an effect, but only run once cause redux list won't change unless do over
  useEffect(() => {
    dispatch({ type: 'INIT_LIST', payload: resitCandidateData } as CoordinatorResitAction);
  }, [resitCandidateData]);

  // table row data
  const candidateTableRows = useMemo(() => {
    if (!state.resitCandidates) {
      return [];
    }
    return state.candidateID.map((key) => ({
      name: state.resitCandidates?.[key].statutoryName,
      id: state.resitCandidates?.[key].nricFin,
    }));
  }, [state.candidateID, state.resitCandidates]);

  // for candidate listing
  const [isAddCandidate, setIsAddCandidate] = useState(defaultAdding);

  // group review declaration
  const [isAgreed, setIsAgreed] = useState(false);
  const [continueStatus, setContinueStatue] = useState<MutationStatus>('idle');
  const [feeProcessingError, setFeeProcessingError] = useState('');

  const postStartProcessing = useResitFeeProcessing(
    resitUuid || '',
    () => setStep(step + 1),
    setContinueStatue,
    setFeeProcessingError
  );

  const resitFeeProcessing = useCallback(() => {
    if (resitUuid && state.resitCandidates) {
      setContinueStatue('loading');

      const candidateResitSubjects = Object.entries(state.resitCandidates).map((entry) => {
        const selectedSubject = entry[1].subjects[entry[1].selectedSubjects[0]].split('-');

        const candidateId = selectedSubject[0];
        const subjectCode = selectedSubject[1].split('::')[0];

        return { candidateId, subjectCode };
      });

      setFeeProcessingError('');
      postStartProcessing({ uuid: resitUuid, candidateResitSubjects });
    }
  }, [resitUuid, postStartProcessing, state.resitCandidates]);

  const stepNavigate = useCallback(
    (forward: boolean) => {
      switch (step) {
        case 0: {
          if (forward) {
            setIsAddCandidate(false);
            setStep(step + 1);
          } else {
            history.push(toCoordinatorResitRegister);
          }
          break;
        }
        case 1: {
          if (forward) {
            resitFeeProcessing();
          } else {
            setStep(step - 1);
          }
          break;
        }
        case 2: {
          if (forward) {
            setStep(step + 1);
          } else {
            setStep(step - 1);
          }
          break;
        }
        case 3: {
          if (forward) {
            // do confirmation
          } else {
            setIsAgreed(false);
            setStep(step - 1);
          }
          break;
        }
        default: {
          break;
        }
      }
    },
    [history, step, toCoordinatorResitRegister, resitFeeProcessing]
  );

  return {
    step,
    setStep,
    state,
    dispatch,
    candidateTableRows,
    isAddCandidate,
    setIsAddCandidate,
    isAgreed,
    setIsAgreed,
    stepNavigate,
    continueStatus,
    feeProcessingError,
  };
};

const useResitFeeProcessing = (
  resitUuid: string,
  nextStep: () => void,
  setContinueStatue: (continueStatue: MutationStatus) => void,
  setFeeProcessingError: (feeProcessingError: string) => void
) => {
  const [enableGetFeeProcessingStatus, setEnableGetFeeProcessingStatus] = useState(false);

  useGetFeeProcessingStatus({
    uuid: resitUuid,
    onSuccess: (feeProcessingStatus) => {
      setEnableGetFeeProcessingStatus(
        feeProcessingStatus.status === ProcessingFileStatus.PROCESSING
      );
      if (feeProcessingStatus.status === ProcessingFileStatus.SUCCESS) {
        nextStep();
        setContinueStatue('success');
      }
      if (feeProcessingStatus.status === ProcessingFileStatus.ERROR) {
        setFeeProcessingError(feeProcessingStatus.errorMessage);
        setContinueStatue('error');
      }
    },
    enabled: enableGetFeeProcessingStatus,
  });

  const { mutate: postStartProcessing } = usePostStartProcessing({
    onSuccess: () => setEnableGetFeeProcessingStatus(true),
    onError: error => {
      setFeeProcessingError(
        error.response?.data?.message ||
          'Unable to process the re-sit registration. Please try again.'
      );
      setContinueStatue('error');
    },
  });

  return postStartProcessing;
};

export const useGroupFeeApis = () => {
  const [sendGroupFeeError, setSendGroupFeeError] = useState('');
  const [sendEmailError, setSendEmailError] = useState('');

  const postConfirmAndPayMutation = usePostConfirmAndPay({
    onError: () => {
      setSendGroupFeeError(
        'An error occurred while trying to perform this operation. Please retry.'
      );
    },
  });

  return {
    sendGroupFeeError,
    setSendGroupFeeError,
    sendEmailError,
    setSendEmailError,
    postConfirmAndPayMutation,
  };
};

export const useResitAccess = (instructionConsent = true) => {
  // Redux examl level
  const examRedux = useSelector(selectCandidateResitRegistration);

  const { data: accessData } = candidateService.resit.coordinator.useCoordinatorResitAccess(
    () => {
      // Do Nothing
    },
    () => {
      // Do Nothing
    },
    instructionConsent && examRedux.examLevel?.code
  );

  return {
    examRedux,
    accessData,
  };
};
